/**
 * The START A CREW wizard — three steps, one transaction at the end.
 * `design/GT Crew.dc.html` / `design/GT Mobile Crew.dc.html`
 * (`data-screen-label="Start a crew"`), DESIGN-CHANGELOG 2026-08-18.
 *
 * Step 1 ACCOUNT · step 2 THE CREW · step 3 INVITES.
 *
 * TWO ENTRY MODES, and they differ by more than a starting step. A signed-in
 * account starting another crew enters at step 2 and never gets a clickable
 * step 1 — not because it is locked, but because it has no account form in its
 * flow at all. `entryStep` is what remembers which flow this is.
 *
 * STEPS 1 AND 2 LOCK ON STEP 3, with the reason on hover. `CREATE CREW` commits
 * the account and the crew together, so by step 3 there is no in-progress form
 * to go back to — the account exists and the crew exists. Renaming happens in
 * MANAGE CREW. The step you are standing on says nothing, because it is not
 * finished.
 */

import type { StateCreator } from 'zustand';

import { crewSlugBase } from '../../lib/crewSlug.ts';
import { RepoError } from '../../repo/Repository.ts';
import type { QdStore } from '../types.ts';

/** supabase/config.toml [auth] minimum_password_length. */
export const WIZARD_PASSWORD_MIN = 8;

export interface WizardRailStep {
  num: string;
  label: string;
  /** Current step. */
  on: boolean;
  /** Reachable by clicking — a passed step in a flow that still owns it. */
  can: boolean;
  /** Hover reason when it is not; `''` on the step you are standing on. */
  tip: string;
}

export interface WizardSlice {
  wzStep: 1 | 2 | 3;
  /** 1 signed-out, 2 signed-in. A signed-in flow never has a step 1. */
  wzEntryStep: 1 | 2;
  wzName: string;
  wzEmail: string;
  wzP1: string;
  wzP2: string;
  wzCrew: string;
  wzErr: string;
  wzBusy: boolean;
  /** Step 3's invite form + what it has produced so far. */
  wzIName: string;
  wzIEmail: string;
  wzIErr: string;
  /** Addresses invited from step 3. Just addresses now — there is no account
   *  and no password to show until each person accepts. */
  wzInvites: string[];

  openWizard: () => void;
  setWz: (patch: Partial<QdStore>) => void;
  wzRail: () => WizardRailStep[];
  wzNext1: () => void;
  wzGoStep: (n: 1 | 2 | 3) => void;
  wzCreate: () => Promise<void>;
  wzAddInvite: () => Promise<void>;
  wzFinish: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

const blank = {
  wzStep: 1 as const,
  wzName: '',
  wzEmail: '',
  wzP1: '',
  wzP2: '',
  wzCrew: '',
  wzErr: '',
  wzBusy: false,
  wzIName: '',
  wzIEmail: '',
  wzIErr: '',
  wzInvites: [] as string[],
};

export const createWizardSlice: StateCreator<QdStore, [], [], WizardSlice> = (set, get) => ({
  ...blank,
  wzEntryStep: 1,

  openWizard: () => {
    // Signed in means the account half is already done and is not part of this
    // flow — enter at THE CREW, and never offer a step 1 that would have
    // nothing to show.
    const signedIn = Boolean(get().session);
    set({ ...blank, wzStep: signedIn ? 2 : 1, wzEntryStep: signedIn ? 2 : 1 });
    get().nav('startCrew');
  },

  setWz: (patch) => set(patch),

  wzRail: () => {
    const s = get();
    const locked = s.wzStep === 3;
    const labels: Array<[string, string]> = [
      ['1', 'ACCOUNT'],
      ['2', 'THE PARTY'],
      ['3', 'INVITES'],
    ];
    return labels.map(([num, label], i) => {
      const n = (i + 1) as 1 | 2 | 3;
      const on = s.wzStep === n;
      // A signed-in flow owns no step 1, so it is never reachable and says so
      // with nothing — there is no account form to go back to, ever.
      const ownsStep = n >= s.wzEntryStep;
      const passed = n < s.wzStep && ownsStep;
      return {
        num,
        label,
        on,
        can: passed && !locked,
        tip: on
          ? ''
          : locked && n === 1
            ? 'ACCOUNT IS LOCKED — IT EXISTS NOW'
            : locked && n === 2
              ? 'LOCKED — THE PARTY EXISTS NOW'
              : '',
      };
    });
  },

  wzNext1: () => {
    const s = get();
    const name = s.wzName.trim();
    if (!name) return set({ wzErr: 'YOUR NAME IS REQUIRED' });
    if (!EMAIL_RE.test(s.wzEmail.trim())) return set({ wzErr: "THAT DOESN'T LOOK LIKE AN EMAIL" });
    if (s.wzP1.length < WIZARD_PASSWORD_MIN) {
      return set({ wzErr: `USE AT LEAST ${WIZARD_PASSWORD_MIN} CHARACTERS` });
    }
    if (s.wzP1 !== s.wzP2) return set({ wzErr: 'PASSWORDS DO NOT MATCH' });
    set({ wzErr: '', wzStep: 2 });
  },

  wzGoStep: (n) => {
    const step = get().wzRail()[n - 1];
    if (!step?.can) return;
    set({ wzStep: n, wzErr: '' });
  },

  wzCreate: async () => {
    const s = get();
    if (s.wzBusy) return;
    const name = s.wzCrew.trim();
    if (!name) return set({ wzErr: 'NAME YOUR PARTY' });
    /* "TWO LETTERS OR NUMBERS" OUTLIVED ITS REASON, AND STAYS ANYWAY (3031).
       It existed because the address was derived from the name, so a name with
       nothing latin in it — `日本`, `!!!` — yielded no usable slug. The address
       is opaque now and the database would accept those names.

       But `crew-create` still applies the same rule, and relaxing that means
       deploying a third edge function into a release that already carries two.
       Dropping it here alone would only move the refusal from this line to a
       server round trip, which is strictly worse. It goes when the function
       does, in phase E. */
    if (crewSlugBase(name).length < 2) {
      return set({ wzErr: 'USE AT LEAST TWO LETTERS OR NUMBERS' });
    }

    set({ wzBusy: true, wzErr: '' });
    const created = await get().startCrew({
      name,
      // The signed-in flow has no account half; sending one would be asking
      // the server to create a second account for somebody who has one.
      ...(s.wzEntryStep === 1
        ? {
            account: {
              displayName: s.wzName.trim(),
              email: s.wzEmail.trim(),
              password: s.wzP1,
            },
          }
        : {}),
    });
    set({ wzBusy: false });

    if (!created) {
      // `startCrew` already toasted. Stay on step 2 with the form intact —
      // nothing was committed, so this is still an in-progress form.
      set({ wzErr: "COULDN'T START THAT PARTY — TRY AGAIN" });
      return;
    }
    // The account and the crew both exist now, which is exactly why steps 1
    // and 2 lock from here.
    //
    // `startCrew` navigated to Tonight on success, because every other caller
    // wants that. The wizard does not: step 3 is where invites happen.
    set({ wzStep: 3, wzErr: '', wzP1: '', wzP2: '' });
    get().nav('startCrew');
  },

  /**
   * Step 3's invite, which is now THE SAME ACT as inviting from MANAGE CREW —
   * an address and nothing else (3026, docs/INVITES.md).
   *
   * It used to call `doAddUser`, minting a real account with a name the owner
   * typed and a temp password to hand over. Leaving it that way would have put
   * two different invite mechanisms in one product: a crew founded through the
   * wizard would hand out passwords while the same owner, one screen later,
   * sent proper invitations.
   */
  wzAddInvite: async () => {
    const s = get();
    if (s.wzBusy) return;
    const email = s.wzIEmail.trim();
    if (!EMAIL_RE.test(email)) return set({ wzIErr: 'A REAL EMAIL IS REQUIRED' });

    set({ wzBusy: true, wzIErr: '' });
    try {
      await s.repo.inviteMember(email);
      set((st) => ({ wzInvites: [...st.wzInvites, email], wzIName: '', wzIEmail: '' }));
    } catch (e) {
      const code = e instanceof RepoError ? e.code : '';
      set({
        wzIErr:
          code === 'ALREADY_MEMBER'
            ? 'THEY ARE ALREADY IN THIS PARTY'
            : code === 'EMAIL_INVALID'
              ? "THAT DOESN'T LOOK LIKE AN EMAIL"
              : "COULDN'T INVITE THEM — TRY AGAIN",
      });
    } finally {
      set({ wzBusy: false });
    }
  },

  wzFinish: () => {
    set({ ...blank, wzEntryStep: get().wzEntryStep });
    // The wizard lands on MY GAMES like every other way in (2026-09-03).
    get().nav('mine');
  },
});
